/**
 * BackToTop — floating, semi-translucent "to top" button.
 *
 * Appears once the user has scrolled past a threshold; smooth-scrolls to the
 * top on click. Hidden on splash routes. Lifted above the mobile tab bar so
 * it never overlaps the bottom nav, and tucked into the bottom-left so it
 * stays clear of the bottom-right "Made with Emergent" badge.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronUp } from "lucide-react";

const SHOW_THRESHOLD = 400; // px scrolled before the button fades in
const HIDE_ON = new Set(["/", "/login", "/coming-soon", "/enter"]);

export default function BackToTop() {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_THRESHOLD);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Re-evaluate on route change too — a freshly mounted page starts at 0.
  useEffect(() => {
    setVisible(window.scrollY > SHOW_THRESHOLD);
  }, [location.pathname]);

  if (HIDE_ON.has(location.pathname)) return null;

  const scrollUp = () => window.scrollTo({ top: 0, behavior: "smooth" });

  return (
    <button
      type="button"
      onClick={scrollUp}
      aria-label="Back to top"
      title="Back to top"
      data-testid="back-to-top"
      className={`fixed z-40 left-5 sm:left-8 bottom-24 md:bottom-8
        w-11 h-11 sm:w-12 sm:h-12 rounded-full
        flex items-center justify-center
        bg-background/70 backdrop-blur-md border border-border/70 shadow-md
        text-foreground/70 hover:text-[hsl(var(--primary))]
        hover:bg-background/90 hover:border-[hsl(var(--primary))]
        active:scale-95
        transition-all duration-200
        ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-2 pointer-events-none"}`}
    >
      <ChevronUp size={20} strokeWidth={1.7} />
    </button>
  );
}
